<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="mb-6">
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Kitchen Orders
        </h1>
        <p class="text-gray-600 dark:text-gray-400">
          Manage and prepare kitchen orders
        </p>
      </div>

      <div class="mb-6 flex gap-3 flex-wrap">
        <button
          v-for="status in ['', 'Pending', 'Preparing']"
          :key="status"
          @click="filterOrders(status)"
          class="px-4 py-2 rounded-lg font-medium transition-all"
          :class="
            filterStatus === status
              ? 'bg-blue-600 text-white shadow-md'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          "
        >
          {{ status.charAt(0).toUpperCase() + status.slice(1) }}
          <span
            v-if="status !== 'all'"
            class="ml-2 px-2 py-0.5 rounded-full text-xs"
            :class="
              filterStatus === status
                ? 'bg-blue-700'
                : 'bg-gray-200 dark:bg-gray-700'
            "
          >
            {{ getStatusCount(status) }}
          </span>
        </button>
      </div>

      <div v-if="loading" class="text-center py-12">
        <div
          class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"
        ></div>
        <p class="mt-4 text-gray-600 dark:text-gray-400">Loading orders...</p>
      </div>

      <div v-else-if="filteredOrders.length === 0" class="text-center py-12">
        <svg
          class="mx-auto h-16 w-16 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <p class="mt-4 text-lg text-gray-600 dark:text-gray-400">
          No orders found
        </p>
      </div>

      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div
          v-for="order in filteredOrders"
          :key="order.id"
          @click="openOrderDetails(order)"
          class="bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-xl transition-all cursor-pointer border-2"
          :class="{
            'border-yellow-400': order.status === 'pending',
            'border-orange-400': order.status === 'preparing',
            'border-green-400': order.status === 'ready',
            'border-gray-300 dark:border-gray-600':
              order.status === 'completed' || order.status === 'cancelled',
          }"
        >
          <div class="p-5">
            <div class="flex justify-between items-start mb-3">
              <div>
                <h3 class="text-xl font-bold text-gray-900 dark:text-white">
                  Order No: {{ order.id }}
                </h3>
                <p class="text-sm text-gray-500 dark:text-gray-400">
                  {{ formatTime(order.created_at) }}
                </p>
              </div>
              <span
                class="px-3 py-1 rounded-full text-xs font-semibold"
                :class="{
                  'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300':
                    order.status === 'pending',
                  'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300':
                    order.status === 'preparing',
                  'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300':
                    order.status === 'ready',
                  'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300':
                    order.status === 'completed' ||
                    order.status === 'cancelled',
                }"
              >
                {{ order.status.toUpperCase() }}
              </span>
            </div>

            <div class="space-y-2 mb-4">
              <div
                v-if="order.invoice_number"
                class="flex items-center text-sm text-gray-700 dark:text-gray-300"
              >
                <svg
                  class="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Inv No: {{ order.invoice_number }}
              </div>
              <div
                v-if="order.table_number"
                class="flex items-center text-sm text-gray-700 dark:text-gray-300"
              >
                <svg
                  class="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
                Table: {{ order.table_number }}
              </div>

              <div
                v-if="order.customer_name"
                class="flex items-center text-sm text-gray-700 dark:text-gray-300"
              >
                <svg
                  class="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
                {{ order.customer_name }}
              </div>

              <div class="flex items-center text-sm" v-if="order.order_type">
                <svg
                  class="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                  />
                </svg>
                <span class="capitalize text-gray-700 dark:text-gray-300">{{
                  order.order_type
                }}</span>
              </div>
            </div>

            <div
              v-if="order.notes"
              class="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg"
            >
              <p
                class="text-sm text-yellow-800 dark:text-yellow-300 flex items-start"
              >
                <svg
                  class="w-4 h-4 mr-2 mt-0.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {{ order.notes }}
              </p>
            </div>

            <div
              class="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700"
            >
              <span class="text-sm text-gray-500 dark:text-gray-400">
                {{ order.kot_items?.length || 0 }} items
              </span>
              <span class="text-lg font-bold text-gray-900 dark:text-white">
              </span>
            </div>

            <div
              v-if="order.priority === 'high' || order.priority === 'urgent'"
              class="mt-3"
            >
              <span
                class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold"
                :class="{
                  'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300':
                    order.priority === 'urgent',
                  'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300':
                    order.priority === 'high',
                }"
              >
                <svg
                  class="w-3 h-3 mr-1"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clip-rule="evenodd"
                  />
                </svg>
                {{ order.priority.toUpperCase() }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="selectedOrder"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      @click.self="closeOrderDetails"
    >
      <div
        class="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
      >
        <div
          class="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-start"
        >
          <div>
            <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {{ selectedOrder.order_number }}
            </h2>
            <div class="flex gap-4 text-sm text-gray-600 dark:text-gray-400">
              <span>{{ formatTime(selectedOrder.created_at) }}</span>
              <span v-if="selectedOrder.table_number"
                >Table: {{ selectedOrder.table_number }}</span
              >
              <span v-if="selectedOrder.customer_name">{{
                selectedOrder.customer_name
              }}</span>
            </div>
          </div>
          <button
            @click="closeOrderDetails"
            class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg
              class="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div class="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <div
            v-if="selectedOrder.notes"
            class="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg"
          >
            <h3 class="font-semibold text-yellow-900 dark:text-yellow-200 mb-2">
              Special Instructions
            </h3>
            <p class="text-yellow-800 dark:text-yellow-300">
              {{ selectedOrder.notes }}
            </p>
          </div>

          <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Order Items
          </h3>

          <div class="space-y-3">
            <div
              v-for="item in selectedOrder.kot_items"
              :key="item.id"
              class="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4"
            >
              <div class="flex justify-between items-start mb-2">
                <div class="flex-1">
                  <h4 class="font-semibold text-gray-900 dark:text-white">
                    {{ item.item_name }}
                  </h4>
                  <p
                    v-if="item.item_code"
                    class="text-sm text-gray-500 dark:text-gray-400"
                  >
                    SKU: {{ item.item_code }}
                  </p>
                </div>
                <span
                  class="ml-4 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
                  :class="{
                    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300':
                      item.status.toLowerCase() === 'pending',
                    'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300':
                      item.status.toLowerCase() === 'preparing',
                    'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300':
                      item.status === 'ready',
                  }"
                >
                  {{ item.status.toUpperCase() }}
                </span>
              </div>

              <div
                v-if="item.special_instructions"
                class="mb-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded"
              >
                <p class="text-sm text-yellow-800 dark:text-yellow-300">
                  Note: {{ item.special_instructions }}
                </p>
              </div>

              <div class="flex justify-between items-center">
                <span class="text-gray-600 dark:text-gray-400">
                  Quantity:
                  <span class="font-semibold text-gray-900 dark:text-white">{{
                    item.quantity
                  }}</span>
                </span>
                <div class="flex justify-end gap-2">
                  <button
                    v-if="item.status.toLowerCase() === 'pending'"
                    @click="updateOrderStatus(selectedOrder.id, 'item', 'Preparing', item.id)"
                    class="px-3 py-1.5 bg-orange-600 text-white rounded-lg text-sm font-semibold hover:bg-orange-700 transition-colors"
                  >
                    Start Preparing
                  </button>

                  <button
                    v-if="
                      item.status.toLowerCase() === 'pending' ||
                      selectedOrder.status.toLowerCase() === 'preparing'
                    "
                    @click="updateOrderStatus(selectedOrder.id, 'item', 'Ready', item.id)"
                    class="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors"
                  >
                    Mark as Ready
                  </button>
                </div>
                <!--× ${{ Number(item.unit_price).toFixed(2) }}
                </span>
                <span class="font-bold text-gray-900 dark:text-white">
                  ${{ Number(item.total_price).toFixed(2) }}
                </span>-->
              </div>
            </div>
          </div>

          <!--<div
            class="mt-6 pt-4 border-t-2 border-gray-200 dark:border-gray-700"
          >
            <div class="flex justify-between items-center text-xl font-bold">
              <span class="text-gray-900 dark:text-white">Total Amount</span>
              <span class="text-gray-900 dark:text-white">
                ${{ Number(selectedOrder.total_amount).toFixed(2) }}
              </span>
            </div>
          </div>-->
        </div>

        <div
          class="p-6 border-t border-gray-200 dark:border-gray-700 flex gap-3"
        >
          <button
            v-if="selectedOrder.status.toLowerCase() === 'pending'"
            @click="updateOrderStatus(selectedOrder.id, 'order', 'Preparing', '')"
            class="flex-1 px-6 py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors"
          >
            Start Preparing
          </button>

          <button
            v-if="
              selectedOrder.status.toLowerCase() === 'pending' ||
              selectedOrder.status.toLowerCase() === 'preparing'
            "
            @click="updateOrderStatus(selectedOrder.id, 'order', 'Ready', '')"
            class="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
          >
            Mark as Ready
          </button>

          <!--<button
            v-if="selectedOrder.status.toLowerCase() === 'ready'"
            @click="updateOrderStatus(selectedOrder.id, 'order', 'Completed', '')"
            class="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            Complete Order
          </button>-->

          <button
            @click="closeOrderDetails"
            class="px-6 py-3 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg font-semibold hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import Toast from "../../../../services/Toast";
import { api } from "../../../../api/Api";
import { isEmpty } from "../../../../utils/CommonFunctions";

interface OrderItem {
  id: string;
  items_id: string;
  order_id: string;
  item_name: string;
  item_code: string | null;
  quantity: number;
  unit_price: number;
  total_price: number;
  special_instructions: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

interface KitchenOrder {
  id: string;
  order_number: string;
  invoice_number?: string | null;
  table_number: string | null;
  customer_name: string | null;
  order_type: "dine-in" | "takeaway" | "delivery";
  status: string;
  priority: "normal" | "high" | "urgent";
  notes: string | null;
  total_amount: number;
  created_by: string | null;
  prepared_by: string | null;
  prepared_at: string | null;
  created_at: string;
  updated_at: string;
  kot_items?: OrderItem[];
}

const loading = ref(false);
const orders = ref<KitchenOrder[]>([]);
const selectedOrder = ref<KitchenOrder | null>(null);
const filterStatus = ref<"all" | "pending" | "preparing" | "ready">("all");

const filteredOrders = computed(() => {
  if (filterStatus.value === "all") {
    return orders.value.filter(
      (o) => o.status !== "completed" && o.status !== "cancelled"
    );
  }
  return orders.value.filter((o) => o.status === filterStatus.value);
});

const getStatusCount = (status: string) => {
  return orders.value.filter((o) => o.status === status).length;
};

const formatTime = (timestamp: string) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} min ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ${diffMins % 60}m ago`;

  return date.toLocaleString();
};


const searchQuery = ref("");
const loadOrders = async () => {
  const params = {
    search_query: searchQuery.value || "",
    status: status.value || "",
    statuses: isEmpty(status.value) ? ["Pending", "Preparing"] : "",
  };
  const response = await api.post("revenue/sales/pos/kitchen-orders", params);
  if (response.success == true) {
    orders.value = response.data;
  } else {
    Toast.show("Item not found", "warning");
  }
};

const openOrderDetails = async (order: KitchenOrder) => {
  selectedOrder.value = order;
};

const closeOrderDetails = () => {
  selectedOrder.value = null;
};

const status = ref("");
const updateOrderStatus = async (orderId: string, type: string, newStatus: string, order_item_id: string | null) => {
  const params = {
    search_query: searchQuery.value || "",
    orders_id: orderId,
    id: order_item_id,
    updateStatus: newStatus,
    status: status.value || "",
    statuses: isEmpty(status.value) ? ["Pending", "Preparing", "Ready"] : "",
  };
  const response = await api.post(
    "revenue/sales/pos/kitchen-" + type + "-status",
    params
  );
  if (response.success == true) {
    Toast.show(`Order status updated to ${newStatus}`, "info");
    orders.value = response.data;
    const ord = orders.value.find(o => o.id === orderId);
    if (ord) {
      openOrderDetails(ord);
    }

  }
};

const filterOrders = (st: string) => {
  status.value = st;
  loadOrders();  
};


onMounted(() => {
  loadOrders();
});
</script>
